(function () {
  class ExpandingImageSlider {
    constructor(root) {
      this.root = root;
      this.section = root.closest('.eis-section');
      this.track = root.querySelector('.eis-track');
      if (!this.section || !this.track) return;

      this.slides = Array.from(root.querySelectorAll('.eis-slide'));
      this.prevBtn = root.querySelector('.csss-nav--prev');
      this.nextBtn = root.querySelector('.csss-nav--next');
      this.dots = Array.from(root.querySelectorAll('[data-eis-dot]'));

      this.currentIndex = 0;
      this.minWidth = parseFloat(root.dataset.minWidth) || 80;
      this.maxWidth = parseFloat(root.dataset.maxWidth) || 100;
      this.minHeight = parseFloat(root.dataset.minHeight) || this.minWidth;
      this.maxHeight = parseFloat(root.dataset.maxHeight) || this.maxWidth;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.currentWidth = this.minWidth;
      this.targetWidth = this.minWidth;
      this.currentHeight = this.minHeight;
      this.targetHeight = this.minHeight;
      this.hasUserScrolled = (window.scrollY || 0) > 2;
      this.rafLoop = null;
      this.visible = true;
      this._slideTransitionId = 0;
      this._slideTransitionTimer = null;
      this._slideTransitionOnEnd = null;
      this._slideTransitionOutgoing = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.loop = this.loop.bind(this);

      this.prevBtn?.addEventListener('click', () => this.step(-1));
      this.nextBtn?.addEventListener('click', () => this.step(1));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.eisDot, 10);
          if (!isNaN(idx)) this.goTo(idx);
        });
      });

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            this.visible = entries.some((e) => e.isIntersecting);
            if (this.visible) this.startLoop();
          },
          { rootMargin: '50px' }
        );
        this.io.observe(this.section);
      }

      this.onResize();
      if (this.hasUserScrolled) {
        this.updateTarget();
        this.currentWidth = this.targetWidth;
        this.currentHeight = this.targetHeight;
      } else {
        this.targetWidth = this.minWidth;
        this.currentWidth = this.minWidth;
        this.targetHeight = this.minHeight;
        this.currentHeight = this.minHeight;
      }
      this.applyDimensions();
      this.updateActiveSlide();
      this.updateDots();
      this.startLoop();
    }

    totalSlides() {
      return this.slides.length;
    }

    step(direction) {
      const total = this.totalSlides();
      if (!total) return;
      const prevIndex = this.currentIndex;
      this.currentIndex = (this.currentIndex + direction + total) % total;
      this.updateDots();
      this.updateActiveSlide(direction, prevIndex);
    }

    goTo(index) {
      const total = this.totalSlides();
      if (!total) return;
      const prevIndex = this.currentIndex;
      const nextIndex = ((index % total) + total) % total;
      if (nextIndex === prevIndex) return;

      let direction = 1;
      if (nextIndex > prevIndex) {
        direction = 1;
      } else if (nextIndex < prevIndex) {
        direction = -1;
      }

      this.currentIndex = nextIndex;
      this.updateDots();
      this.updateActiveSlide(direction, prevIndex);
    }

    updateDots() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.currentIndex);
      });
    }

    clearSlideMotionClasses(slide) {
      slide.classList.remove(
        'is-active',
        'is-leaving',
        'is-entering',
        'is-leave-left',
        'is-leave-right',
        'is-enter-right',
        'is-enter-left'
      );
    }

    cancelSlideTransition() {
      if (this._slideTransitionTimer) {
        clearTimeout(this._slideTransitionTimer);
        this._slideTransitionTimer = null;
      }
      if (this._slideTransitionOnEnd && this._slideTransitionOutgoing) {
        this._slideTransitionOutgoing.removeEventListener(
          'transitionend',
          this._slideTransitionOnEnd
        );
      }
      this._slideTransitionOnEnd = null;
      this._slideTransitionOutgoing = null;
    }

    setActiveSlideInstant(index) {
      const total = this.totalSlides();
      const idx = ((index % total) + total) % total;
      this.slides.forEach((slide, i) => {
        this.clearSlideMotionClasses(slide);
        if (i === idx) slide.classList.add('is-active');
      });
    }

    updateActiveSlide(direction = 0, prevIndex = null) {
      if (!this.slides.length) return;

      const total = this.totalSlides();
      const idx = ((this.currentIndex % total) + total) % total;
      const fromIndex = prevIndex === null ? idx : prevIndex;

      this.cancelSlideTransition();

      if (this.reducedMotion || direction === 0 || fromIndex === idx) {
        this._slideTransitionId += 1;
        this.setActiveSlideInstant(idx);
        return;
      }

      const transitionId = (this._slideTransitionId += 1);
      const outgoing = this.slides[fromIndex];
      const incoming = this.slides[idx];

      this.slides.forEach((slide) => this.clearSlideMotionClasses(slide));

      outgoing.classList.add('is-active', 'is-leaving');
      incoming.classList.add('is-entering');

      if (direction > 0) {
        incoming.classList.add('is-enter-right');
      } else {
        incoming.classList.add('is-enter-left');
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (transitionId !== this._slideTransitionId) return;
          if (direction > 0) {
            outgoing.classList.add('is-leave-left');
          } else {
            outgoing.classList.add('is-leave-right');
          }
          incoming.classList.add('is-active');
        });
      });

      const finishTransition = () => {
        if (transitionId !== this._slideTransitionId) return;
        this.cancelSlideTransition();
        this.setActiveSlideInstant(this.currentIndex);
      };

      if (!outgoing) {
        finishTransition();
        return;
      }

      this._slideTransitionOutgoing = outgoing;
      this._slideTransitionOnEnd = (event) => {
        if (transitionId !== this._slideTransitionId) return;
        if (event.target !== outgoing || event.propertyName !== 'transform') return;
        finishTransition();
      };
      outgoing.addEventListener('transitionend', this._slideTransitionOnEnd);
      this._slideTransitionTimer = setTimeout(finishTransition, 900);
    }

    onResize() {
      if (this.hasUserScrolled) {
        this.updateTarget();
      }
      this.applyDimensions();
      this.startLoop();
    }

    onScroll() {
      this.hasUserScrolled = true;
      this.updateTarget();
      this.startLoop();
    }

    updateTarget() {
      if (!this.hasUserScrolled) {
        this.targetWidth = this.minWidth;
        this.targetHeight = this.minHeight;
        return;
      }
      if (this.reducedMotion) {
        this.targetWidth = this.maxWidth;
        this.targetHeight = this.maxHeight;
        return;
      }

      const rect = this.section.getBoundingClientRect();
      const viewport = window.innerHeight;
      const totalDistance = viewport + rect.height;
      const traveled = viewport - rect.top;
      const progress = Math.min(1, Math.max(0, traveled / totalDistance));
      const intensity = Math.sin(progress * Math.PI);

      this.targetWidth = this.minWidth + (this.maxWidth - this.minWidth) * intensity;
      this.targetHeight = this.minHeight + (this.maxHeight - this.minHeight) * intensity;
    }

    startLoop() {
      if (!this.visible || this.rafLoop) return;
      this.rafLoop = requestAnimationFrame(this.loop);
    }

    loop() {
      if (!this.visible) {
        this.rafLoop = null;
        return;
      }

      let needsMore = false;
      const widthDiff = this.targetWidth - this.currentWidth;
      const heightDiff = this.targetHeight - this.currentHeight;

      if (Math.abs(widthDiff) > 0.05) {
        this.currentWidth += widthDiff * 0.1;
        needsMore = true;
      } else {
        this.currentWidth = this.targetWidth;
      }

      if (Math.abs(heightDiff) > 0.05) {
        this.currentHeight += heightDiff * 0.1;
        needsMore = true;
      } else {
        this.currentHeight = this.targetHeight;
      }

      this.applyDimensions();

      if (needsMore) {
        this.rafLoop = requestAnimationFrame(this.loop);
      } else {
        this.rafLoop = null;
      }
    }

    applyDimensions() {
      this.section.style.setProperty('--eis-current-width', `${this.currentWidth.toFixed(3)}%`);
      this.section.style.setProperty('--eis-current-height', `${this.currentHeight.toFixed(3)}%`);
    }
  }

  function initExpandingImageSliders() {
    document.querySelectorAll('[data-eis-slider]').forEach((root) => {
      if (root.dataset.eisInitialized === 'true') return;
      root.dataset.eisInitialized = 'true';
      new ExpandingImageSlider(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initExpandingImageSliders);
  } else {
    initExpandingImageSliders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-eis-slider]')) {
      initExpandingImageSliders();
    }
  });
})();
